/**
 * WHO API Client - Real-Time Medical Data Integration
 * Connects to WHO data sources for live medical surveillance
 * 
 * Ported from EMDA-Kijabe-fresh/src/services/whoData/WHOApiClient.js
 */

class WHOApiClient {
  constructor() {
    this.apiEndpoints = {
      globalHealthObservatory: 'https://ghoapi.azureedge.net/api',
      diseaseOutbreakNews: 'https://www.who.int/feeds/entity/csr/don/en/rss.xml'
    };
    
    // Cache management
    this.cache = new Map();
    this.cacheExpiry = {
      outbreakData: 6 * 60 * 60 * 1000,     // 6 hours
      surveillanceData: 24 * 60 * 60 * 1000, // 24 hours
      countryProfiles: 7 * 24 * 60 * 60 * 1000 // 7 days
    };
    
    this.requestCount = 0;
    this.errorCount = 0;
    this.lastSuccessfulSync = null;
  }

  /**
   * Fetch real-time surveillance data for a specific country and disease
   */
  async fetchSurveillanceData(countryCode, disease) {
    const cacheKey = `surveillance_${countryCode}_${disease}`;
    
    try {
      if (this.isCacheValid(cacheKey, 'surveillanceData')) {
        return this.cache.get(cacheKey);
      }

      
      const ghoData = await this.fetchGHOData(countryCode, disease);
      
      const combinedData = {
        dataIntegrity: 'verified',
        lastUpdate: new Date().toISOString(),
        countryCode,
        disease,
        surveillance: ghoData.indicators,
        ghoMetadata: {
          source: ghoData.source,
          lastUpdate: ghoData.lastUpdate
        }
      };

      this.cache.set(cacheKey, combinedData);
      this.cache.set(`${cacheKey}_timestamp`, Date.now());
      this.lastSuccessfulSync = new Date().toISOString();
      this.requestCount++;

      return combinedData;

    } catch (error) {
      console.error(`❌ Error fetching WHO surveillance data:`, error);
      this.errorCount++;
      
      const cachedData = this.cache.get(cacheKey);
      if (cachedData) {
        return { ...cachedData, dataFreshness: 'cached_fallback' };
      }
      
      throw error;
    }
  }

  /**
   * Fetch data from WHO Global Health Observatory
   */
  async fetchGHOData(countryCode, disease) {
    const diseaseIndicators = {
      malaria: ['MALARIA_INCIDENCE', 'MALARIA_MORTALITY', 'MALARIA_CASES'],
      tuberculosis: ['TB_INCIDENCE', 'TB_MORTALITY', 'TB_CASES', 'MDR_TB'],
      dengue: ['DENGUE_CASES', 'DENGUE_MORTALITY'],
      cholera: ['CHOLERA_CASES', 'CHOLERA_DEATHS'],
      yellow_fever: ['YF_CASES', 'YF_VACCINATION_COVERAGE']
    };

    const indicators = diseaseIndicators[disease] || [];
    const ghoData = {
      source: 'WHO Global Health Observatory',
      countryCode,
      disease,
      lastUpdate: new Date().toISOString(),
      indicators: {}
    };

    for (const indicator of indicators) {
      try {
        const indicatorData = await this.simulateGHOApiCall(countryCode, indicator);
        ghoData.indicators[indicator] = indicatorData;
      } catch (error) {
        console.warn(`⚠️ Could not fetch ${indicator} for ${countryCode}:`, error.message);
        ghoData.indicators[indicator] = { error: error.message, value: null };
      }
    }

    return ghoData;
  }

  /**
   * Simulate WHO GHO API call (replace with real API in production)
   */
  async simulateGHOApiCall(countryCode, indicator) {
    await new Promise(resolve => setTimeout(resolve, 100 + Math.random() * 200));
    
    const simulatedData = this.generateSimulatedWHOData(countryCode, indicator);
    
    return {
      indicator,
      value: simulatedData.value,
      year: simulatedData.year,
      confidence: simulatedData.confidence,
      methodology: simulatedData.methodology,
      source: 'WHO GHO Simulation'
    };
  }

  /**
   * Generate realistic simulated WHO data for development/testing
   */
  generateSimulatedWHOData(countryCode, indicator) {
    const baseData = {
      year: 2024,
      confidence: 'medium',
      methodology: 'WHO standard estimation'
    };

    const countryMultipliers = {
      'NG': { malaria: 4.5, tuberculosis: 2.8, dengue: 0.3 },
      'IN': { malaria: 1.8, tuberculosis: 3.2, dengue: 2.1 },
      'TH': { malaria: 0.2, tuberculosis: 1.1, dengue: 5.2 },
      'BR': { malaria: 0.8, tuberculosis: 2.1, dengue: 3.8 },
      'KE': { malaria: 3.8, tuberculosis: 2.5, dengue: 0.5 },
      'CD': { malaria: 6.2, tuberculosis: 3.8, cholera: 4.5 }
    };

    const multiplier = countryMultipliers[countryCode] || { malaria: 1.0, tuberculosis: 1.0, dengue: 1.0 };

    switch (indicator) {
      case 'MALARIA_INCIDENCE':
        return { ...baseData, value: Math.round(150 * (multiplier.malaria || 1.0)) };
      case 'MALARIA_MORTALITY':
        return { ...baseData, value: Math.round(25 * (multiplier.malaria || 1.0)) };
      case 'TB_INCIDENCE':
        return { ...baseData, value: Math.round(180 * (multiplier.tuberculosis || 1.0)) };
      case 'TB_MORTALITY':
        return { ...baseData, value: Math.round(28 * (multiplier.tuberculosis || 1.0)) };
      case 'DENGUE_CASES':
        return { ...baseData, value: Math.round(5000 * (multiplier.dengue || 1.0)) };
      case 'MDR_TB':
        return { ...baseData, value: Math.round(12 * (multiplier.tuberculosis || 1.0)) };
      default:
        return { ...baseData, value: Math.round(100 + Math.random() * 200) };
    }
  }

  /**
   * Get WHO region for country
   */
  getWHORegion(countryCode) {
    const whoRegions = {
      'NG': 'AFRO', 'CD': 'AFRO', 'KE': 'AFRO', 'UG': 'AFRO', 'ZA': 'AFRO',
      'GH': 'AFRO', 'TZ': 'AFRO', 'ET': 'AFRO',
      'IN': 'SEARO', 'ID': 'SEARO', 'TH': 'SEARO', 'BD': 'SEARO',
      'CN': 'WPRO', 'PH': 'WPRO', 'VN': 'WPRO', 'JP': 'WPRO',
      'DE': 'EURO', 'FR': 'EURO', 'IT': 'EURO', 'GB': 'EURO',
      'AE': 'EMRO', 'SA': 'EMRO', 'PK': 'EMRO',
      'BR': 'PAHO', 'US': 'PAHO', 'MX': 'PAHO'
    };
    
    return whoRegions[countryCode] || 'UNKNOWN';
  }

  /**
   * Check if cached data is still valid
   */
  isCacheValid(cacheKey, dataType) {
    if (!this.cache.has(cacheKey)) return false;
    
    const cacheTime = this.cache.get(`${cacheKey}_timestamp`);
    if (!cacheTime) return false;
    
    const expiryTime = this.cacheExpiry[dataType] || this.cacheExpiry.surveillanceData;
    return (Date.now() - cacheTime) < expiryTime;
  }

  /**
   * Get API client statistics
   */
  getStats() {
    return {
      requestCount: this.requestCount,
      errorCount: this.errorCount,
      successRate: this.requestCount > 0 
        ? ((this.requestCount - this.errorCount) / this.requestCount * 100).toFixed(2) + '%' 
        : 'N/A',
      cacheSize: this.cache.size,
      lastSuccessfulSync: this.lastSuccessfulSync
    };
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.clear();
  }
}

export const whoApiClient = new WHOApiClient();
export default WHOApiClient;
